import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * Starts checkout. If the customer has a saved default card, the server charges
 * it and fulfils immediately (`paid: true`, no redirect). Otherwise it returns a
 * hosted-checkout URL to redirect to. The frontend never sees card data.
 */
export function useCreateCheckoutSession() {
  return useMutation({
    // paymentMethodId → charge that saved card; undefined → hosted Checkout (new card).
    mutationFn: (paymentMethodId?: string) =>
      api.post<{ paid: boolean; checkout_url: string | null; note?: string }>(
        "/api/payments/checkout-session",
        paymentMethodId ? { payment_method_id: paymentMethodId } : {}
      )
  });
}

// ── Saved cards / payment methods ──────────────────────────────
export interface SavedCard {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

/** Redirects to a Stripe setup page to add a card (no charge). */
export function useAddCardSession() {
  return useMutation({
    mutationFn: () => api.post<{ setup_url: string }>("/api/payments/methods/setup-session")
  });
}

export function usePaymentMethods() {
  return useQuery({
    queryKey: ["payment-methods"],
    queryFn: ({ signal }) => api.get<SavedCard[]>("/api/payments/methods", signal)
  });
}

export function useSetDefaultCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/api/payments/methods/${id}/default`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payment-methods"] })
  });
}

export function useRemoveCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/payments/methods/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payment-methods"] })
  });
}

/**
 * Confirms a completed Checkout Session from the success page. Fulfills the
 * order immediately (idempotent) so it works even if the Stripe webhook is
 * delayed or never arrives in local dev.
 */
export function useConfirmCheckout() {
  return useMutation({
    mutationFn: (sessionId: string) =>
      api.post<{ fulfilled: boolean; note: string }>("/api/payments/confirm", {
        session_id: sessionId
      })
  });
}

/**
 * Pay-by-invoice: issues a send_invoice subscription from the cart and returns
 * the hosted invoice URL. Nothing is provisioned until the invoice is paid.
 */
export function useCreateInvoiceCheckout() {
  return useMutation({
    mutationFn: () =>
      api.post<{ invoice_id: string; hosted_invoice_url: string | null; status: string | null }>(
        "/api/payments/invoice"
      )
  });
}
